// utils/messengerHelpers.js
// ─────────────────────────────────────────────────────────
// Shared helper functions สำหรับ Messenger module
// ─────────────────────────────────────────────────────────

import { WF } from '../utils/messenger-constants';
import { GetDataEmail } from '../utils/data-storage';

// ── Email helper ──────────────────────────────────────────
export const getEmail = () => GetDataEmail?.() || '';

// ── อนุมานสถานะของ "หนึ่ง task" จาก send_start_date/send_end_date/is_complete ──
// รอจัดส่ง >> send_start_date, send_end_date เป็น null
// กำลังส่ง >> มีค่า send_start_date (ยังไม่มี send_end_date)
// ส่งแล้ว  >> มีค่าทั้งสองช่อง, is_complete = 1
// จบงาน    >> มีค่าทั้งสองช่อง, is_complete = 0
export const getTaskStatus = (detail) => {
    if (!detail) return 'pending';
    const hasStarted = !!detail.send_start_date;
    const hasEnded   = !!detail.send_end_date;
    const isDone     = detail.is_complete == 1;

    let status = 'pending';
    if (!hasStarted && !hasEnded) status = 'pending';
    else if (hasStarted && !hasEnded) status = 'inprogress';
    else if (hasEnded && isDone) status = 'done';
    else if (hasEnded && !isDone) status = 'closed';

    // console.log('[getTaskStatus]', {
    //     id: detail.id,
    //     id_request: detail.id_request,
    //     seq: detail.seq,
    //     send_start_date: detail.send_start_date,
    //     send_end_date: detail.send_end_date,
    //     is_complete: detail.is_complete,
    //     '=> status': status,
    // });

    return status;
};

// ── Badge สำหรับ Job card (myjob, overview) ───────────────
// job มีหลาย task (request_details) — รวมสถานะของทุก task เพื่อสรุปเป็น badge เดียว
// ลำดับความสำคัญ: ยกเลิก > จบงาน(closed) > กำลังส่ง > ส่งแล้วครบ > รอจัดส่ง
export const getBadge = (job) => {
    const wfStatus = job?.id_workflow_status;
    const details  = job?.request_details || [];

    // ยกเลิกอ้างจาก wf เพราะไม่มี task field ที่บอกการยกเลิกได้
    if (wfStatus === WF.USER_CANCEL || wfStatus === WF.ADMIN_CANCEL) {
        return { label: 'ยกเลิก', type: 'cancel' };
    }

    if (details.length === 0) {
        return { label: 'รอจัดส่ง', type: 'pending' };
    }

    const statuses = details.map(getTaskStatus);

    // ── มี task ใดๆ "จบงาน" (ขอจบ) → ทั้ง job ถือว่าจบงาน ──
    if (statuses.includes('closed')) {
        return { label: 'จบงาน', type: 'closed' };
    }

    // ── ทุก task เสร็จสมบูรณ์ ("ส่งแล้ว") → job เสร็จ ──
    if (statuses.every(s => s === 'done')) {
        return { label: 'ส่งแล้ว', type: 'done' };
    }

    // ── มี task ที่กำลังส่งอยู่ → job กำลังส่ง ──
    if (statuses.includes('inprogress')) {
        return { label: 'กำลังส่ง', type: 'inprogress' };
    }

    // ── ไม่มี task ไหนเริ่มเลย → รอจัดส่ง ──
    return { label: 'รอจัดส่ง', type: 'pending' };
};

// ── Badge สำหรับแต่ละ stop (complete-multi, incomplete) ───
// ใช้ detail ของ task นั้นๆตรงๆ
export const getStopBadge = (detail) => {
    const status = getTaskStatus(detail);
    switch (status) {
        case 'done':       return { label: 'ส่งแล้ว',  type: 'done'       };
        case 'inprogress': return { label: 'กำลังส่ง', type: 'inprogress' };
        case 'closed':     return { label: 'จบงาน',    type: 'closed'     };
        default:           return { label: 'รอจัดส่ง', type: 'pending'    };
    }
};

const ACTIVE_WF = [5, 6, 9];
const DONE_WF   = [4, 7, 8, 10, 11];

export const getTab = (job) => {
    const wfStatus    = job?.id_workflow_status;
    const serviceDate = job?.service_date;

    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    const sDate = serviceDate ? serviceDate.slice(0, 10) : '';

    // ── งานที่เสร็จแล้ว (7, 8, 10, 11) → tab "done" ──
    if (DONE_WF.includes(wfStatus)) return 'done';

    // ── งานที่ยัง active (5, 6, 9) → แยกตามวันที่ ──
    if (ACTIVE_WF.includes(wfStatus)) {
        if (sDate < today) return 'pending';   // งานค้าง
        if (sDate === today) return 'today';   // งานวันนี้
        return null; // งานล่วงหน้า (ไม่แสดงใน tab ไหน)
    }

    return null; // status อื่นๆ ไม่เข้า tab ไหน
};

// ── ดึงเวลา HH:mm จาก send_end_date (มี offset จริง +07:00 → parse ตรงได้) ──
const toHHMM = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return null;
    return d.toTimeString().slice(0, 5);
};

// ── Time label ────────────────────────────────────────────
// export const getTimeLabel = (job) => {
//     const details = job?.request_details || [];

//     // ── งานส่งเสร็จแล้ว → แสดงเวลาส่งจริง (send_end_date ล่าสุดของทุก step) ──
//     const endDates = details.map(d => d.send_end_date).filter(Boolean);
//     if (details.length > 0 && endDates.length === details.length) {
//         const latest = endDates.reduce((a, b) => (new Date(a) > new Date(b) ? a : b));
//         const time = toHHMM(latest);
//         if (time) return { label: `${time} น.`, type: 'done' };
//     }

//     const now = new Date();
//     const today = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
//     const sDate = job?.service_date?.slice(0, 10) || '';

//     if (sDate < today) return { label: 'เลยกำหนดแล้ว!', type: 'overdue' };
//     if (job?.complete_time_type === 2 && job?.complete_time_specific) {
//         const time = new Date(job.complete_time_specific).toTimeString().slice(0, 5);
//         return { label: `ภายใน ${time} น.`, type: 'normal' };
//     }
//     return { label: 'ภายในวันนี้', type: 'today' };
// };

export const getTimeLabel = (job) => {
    const details = job?.request_details || [];

    // ── Tab งานที่เสร็จแล้ว → แสดงเวลาส่งจริงจาก stop สุดท้าย (seq มากที่สุด) ──
    if (getTab(job) === 'done') {
        const lastStop = details.reduce(
            (max, d) => ((d?.seq ?? 0) > (max?.seq ?? 0) ? d : max),
            null
        );

        const dateTime = lastStop?.send_end_date ? ToThaiDateTime(lastStop.send_end_date) : null;
        return { label: dateTime || '-', type: 'done' };
    }

    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    const sDate = job?.service_date?.slice(0, 10) || '';

    if (sDate < today) return { label: 'เลยกำหนดแล้ว!', type: 'overdue' };
    if (job?.complete_time_type === 2 && job?.complete_time_specific) {
        const time = new Date(job.complete_time_specific).toTimeString().slice(0, 5);
        return { label: `ภายใน ${time} น.`, type: 'normal' };
    }
    return { label: 'ภายในวันนี้', type: 'today' };
};

// ── Time label รายstop — ถ้าส่งแล้วแสดงเวลาส่งจริง ไม่งั้น fallback เป็นของ job ──
export const getStopTimeLabel = (detail, job) => {
    // console.log('[getStopTimeLabel]', { detail, job });
    const time = toHHMM(detail?.send_end_date);
    if (time) return { label: `${time} น.`, type: 'done' };
    return getTimeLabel(job);
};

// ── Format date Thai ──────────────────────────────────────
const THAI_MONTHS = ['ม.ค.','ก.พ.','มี.ค.','เม.ย.','พ.ค.','มิ.ย.','ก.ค.','ส.ค.','ก.ย.','ต.ค.','พ.ย.','ธ.ค.'];

export const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '-';

    return `${d.getDate()} ${THAI_MONTHS[d.getMonth()]} ${d.getFullYear() + 543}`;
};

export const ToThaiDateTime = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;

    const day   = d.getDate();
    const month = THAI_MONTHS[d.getMonth()];
    const year  = d.getFullYear() + 543;
    const time  = `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;

    return `${day} ${month} ${year} - ${time} น.`;
};

// ── Section label ─────────────────────────────────────────
export const getSectionLabel = (section) => {
    switch (section) {
        case 1: return 'รอบเช้า';
        case 2: return 'รอบบ่าย';
        case 3: return 'เอกสารด่วน';
        default: return '-';
    }
};

// ── Document type label ───────────────────────────────────
export const getDocTypeLabel = (type) => {
    switch (type) {
        case 1: return 'รับส่งเอกสาร - รับเท่านั้น';
        case 2: return 'รับส่งเอกสาร - ด่วน';
        case 3: return 'รับส่งเอกสาร - ลับ';
        default: return 'รับส่งเอกสาร';
    }
};

// ── Build incomplete note string ──────────────────────────
export const buildIncompleteNote = (selected, otherText, note, reasons = []) => {
    const reasonLabel = selected === 'other'
        ? otherText.trim()
        : reasons.find(r => String(r.id) === String(selected))?.name || '';
    return note.trim() ? `${reasonLabel} | ${note.trim()}` : reasonLabel;
};